import { useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material"

import {
  Link as RouterLink,
  useLocation,
} from "react-router-dom"

import {
  appConfig,
  getBusinessLabel,
  getDashboardConfig,
  isDashboardVisibleInMenu,
  isModuleEnabled,
} from "../config/appConfig"


// =========================================================
// APPLICATION
// =========================================================

const APP_NAME =
  appConfig.displayName ||
  appConfig.appName ||
  "RLSys Business"

const APP_SUBTITLE =
  appConfig.fullMeaning ||
  "Retail & Logistics System"

const APP_LOGO =
  appConfig.logo ||
  "/rlsys.jpeg"



// =========================================================
// LIBELLÉS MÉTIER
// =========================================================

const MAIN_DISTRIBUTOR = getBusinessLabel(
  "mainDistributor",
  "Distributeur principal"
)

const RELAY_DEPOT = getBusinessLabel(
  "relayDepot",
  "Structure secondaire"
)

const SHOP = getBusinessLabel(
  "shop",
  "Shop"
)

const SHOPS = getBusinessLabel(
  "shops",
  "Shops"
)

const WAREHOUSES = getBusinessLabel(
  "warehouses",
  "Entrepôts"
)

const FIELD_AGENT = getBusinessLabel(
  "fieldAgent",
  "Agent commercial"
)

const FIELD_AGENTS = getBusinessLabel(
  "fieldAgents",
  "Agents commerciaux"
)


// =========================================================
// RÔLES GLOBAUX
// =========================================================

const ALL_ADMIN = [
  "platform_admin",
  "super_admin",
]

const GLOBAL_MANAGEMENT_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
]


// =========================================================
// PARAMÈTRES PRIX
// =========================================================

const PRICE_SETTINGS_ROLES = [
  ...GLOBAL_MANAGEMENT_ROLES,
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// LOGISTIQUE
// =========================================================

const LOGISTICS_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
]


// =========================================================
// PRODUCTION
// =========================================================

const PRODUCTION_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
]


// =========================================================
// RESSOURCES HUMAINES
// =========================================================

const HR_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
]




// =========================================================
// COMPTABILITÉ & FINANCE
// =========================================================

const ACCOUNTING_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "caissier_central",
]


// =========================================================
// MAISON DJO
// =========================================================

const MAISON_DJO_ADMIN_ROLES = [
  "admin_maison_djo",
  "gestionnaire_maison_djo",
]

const MAISON_DJO_STOCK_ROLES = [
  "stock_maison_djo",
  "livreur_maison_djo",
]

const MAISON_DJO_ROLES = [
  ...MAISON_DJO_ADMIN_ROLES,
  ...MAISON_DJO_STOCK_ROLES,
]


// =========================================================
// DÉPÔT RELAIS
// =========================================================

const DEPOT_RELAIS_ADMIN_ROLES = [
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]

const DEPOT_RELAIS_STOCK_ROLES = [
  "stock_depot_relais",
]

const DEPOT_RELAIS_MANAGER_ROLES = [
  ...DEPOT_RELAIS_ADMIN_ROLES,
  ...DEPOT_RELAIS_STOCK_ROLES,
]


// =========================================================
// SHOPS
// =========================================================

const SHOP_MAISON_DJO_ROLES = [
  "gerant_magasin",
]

const SHOP_DEPOT_RELAIS_ROLES = [
  "gerant_depot_relais",
]


// =========================================================
// AGENTS COMMERCIAUX
// =========================================================

const AGENT_ROLES = [
  "agent_commercial",
]


// =========================================================
// PILOTAGE
// =========================================================

const PILOTAGE_GLOBAL_ROLES = [
  ...GLOBAL_MANAGEMENT_ROLES,
  "viewer",
]

const MAISON_DJO_DASHBOARD_ROLES = [
  ...ALL_ADMIN,
  ...MAISON_DJO_ADMIN_ROLES,
]

const DEPOT_RELAIS_DASHBOARD_ROLES = [
  ...GLOBAL_MANAGEMENT_ROLES,
  ...DEPOT_RELAIS_MANAGER_ROLES,
]

const AGENT_DASHBOARD_ROLES = [
  ...GLOBAL_MANAGEMENT_ROLES,
  ...AGENT_ROLES,
]


// =========================================================
// CLIENTS
// =========================================================

const CUSTOMER_ROLES = [
  ...ALL_ADMIN,
  "gerant_magasin",
  "agent_commercial",
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// STRUCTURE
// =========================================================

const STRUCTURE_ROLES = [
  ...ALL_ADMIN,
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// ENTREPÔTS
// =========================================================

const WAREHOUSE_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
  "controleur_stock",
]

const WAREHOUSE_MANAGE_ROLES = [
  ...ALL_ADMIN,
  "gestionnaire_operations",
  "gestionnaire_stock",
]


// =========================================================
// VENTES
// =========================================================

const SALES_VIEW_ROLES = [
  ...ALL_ADMIN,
  ...AGENT_ROLES,
  ...SHOP_MAISON_DJO_ROLES,
  ...SHOP_DEPOT_RELAIS_ROLES,
  "caissier_central",
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]

const SALES_CREATE_ROLES = [
  ...ALL_ADMIN,
  ...AGENT_ROLES,
  ...SHOP_MAISON_DJO_ROLES,
  ...SHOP_DEPOT_RELAIS_ROLES,
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// PAIEMENTS / CAISSE
// =========================================================

const CASHIER_ROLES = [
  ...ALL_ADMIN,
  "caissier_central",
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// DÉPENSES
// =========================================================

const EXPENSE_ROLES = [
  ...ALL_ADMIN,
  ...SHOP_MAISON_DJO_ROLES,
  ...SHOP_DEPOT_RELAIS_ROLES,
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// DETTES
// =========================================================

const DEBT_ROLES = [
  ...ALL_ADMIN,
  "gerant_magasin",
  "caissier_central",
  "gestionnaire_operations",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
]


// =========================================================
// STOCK
// =========================================================

const STOCK_ORDER_ROLES = [
  ...ALL_ADMIN,
  ...MAISON_DJO_ROLES,
  ...DEPOT_RELAIS_MANAGER_ROLES,
  ...SHOP_MAISON_DJO_ROLES,
  ...SHOP_DEPOT_RELAIS_ROLES,
  "gestionnaire_stock",
  "gestionnaire_operations",
]

const STOCK_VIEW_ROLES = [
  ...ALL_ADMIN,
  ...SHOP_MAISON_DJO_ROLES,
  ...SHOP_DEPOT_RELAIS_ROLES,
  "gestionnaire_stock",
  "controleur_stock",
  "gestionnaire_operations",
  ...MAISON_DJO_ROLES,
  ...DEPOT_RELAIS_MANAGER_ROLES,
]

const STOCK_MANAGER_ROLES = [
  ...ALL_ADMIN,
  "gestionnaire_stock",
  "gestionnaire_operations",
  "admin_maison_djo",
  "stock_maison_djo",
  "admin_depot_relais",
  "stock_depot_relais",
]

const STOCK_TRANSFER_ROLES = [
  ...ALL_ADMIN,
  "gestionnaire_stock",
  "gestionnaire_operations",
  "admin_maison_djo",
  "stock_maison_djo",
  "admin_depot_relais",
  "stock_depot_relais",
]

const STOCK_AUDIT_ROLES = [
  ...ALL_ADMIN,
  "gestionnaire_stock",
  "controleur_stock",
  "admin_maison_djo",
  "stock_maison_djo",
  "admin_depot_relais",
  "stock_depot_relais",
]


// =========================================================
// CATALOGUE
// =========================================================

const CATALOGUE_ROLES = [
  ...ALL_ADMIN,
  "gestionnaire_operations",
  "gestionnaire_stock",
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "stock_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
  "stock_depot_relais",
]


// =========================================================
// ACHATS & FOURNISSEURS
// =========================================================

const PURCHASE_VIEW_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
  "controleur_stock",
]

const PURCHASE_REQUEST_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
]

const PURCHASE_ORDER_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
]

const PURCHASE_RECEIPT_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
  "controleur_stock",
]

const SUPPLIER_INVOICE_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "caissier_central",
]

const SUPPLIER_PAYMENT_ROLES = [
  ...ALL_ADMIN,
  "dg",
  "gestionnaire_operations",
  "caissier_central",
]


const PURCHASING_GROUP_ROLES = [
  ...new Set([
    ...PURCHASE_VIEW_ROLES,
    ...PURCHASE_REQUEST_ROLES,
    ...PURCHASE_ORDER_ROLES,
    ...PURCHASE_RECEIPT_ROLES,
    ...SUPPLIER_INVOICE_ROLES,
    ...SUPPLIER_PAYMENT_ROLES,
  ]),
]


// =========================================================
// ADMINISTRATION
// =========================================================

const ADMINISTRATION_ROLES = [
  ...ALL_ADMIN,
]


// =========================================================
// HELPERS MENU
// =========================================================

const dashboardItem = (
  key,
  roles,
  fallbackLabel,
  fallbackPath
) => {
  const dashboard = getDashboardConfig(key)

  if (
    !dashboard ||
    !isDashboardVisibleInMenu(key)
  ) {
    return null
  }

  return {
    text: dashboard.label || fallbackLabel,
    path: dashboard.path || fallbackPath,
    roles,
  }
}


const moduleItem = (
  moduleKey,
  item
) => {
  if (!moduleKey) {
    return item
  }

  return isModuleEnabled(moduleKey)
    ? item
    : null
}


// =========================================================
// GROUPES DU MENU
// =========================================================

const menuGroups = [

  // =======================================================
  // PILOTAGE
  // =======================================================

  {
    title: "Pilotage",

    items: [
      dashboardItem(
        "global",
        PILOTAGE_GLOBAL_ROLES,
        "Dashboard global",
        "/dashboard"
      ),

      dashboardItem(
        "relay",
        DEPOT_RELAIS_DASHBOARD_ROLES,
        `Dashboard ${RELAY_DEPOT}`,
        "/depot-relais-dashboard"
      ),

      dashboardItem(
        "distributor",
        MAISON_DJO_DASHBOARD_ROLES,
        `Dashboard ${MAIN_DISTRIBUTOR}`,
        "/maison-djo-dashboard"
      ),

      dashboardItem(
        "agents",
        AGENT_DASHBOARD_ROLES,
        `Dashboard ${FIELD_AGENTS}`,
        "/agent-commercial-dashboard"
      ),

      dashboardItem(
        "warehouses",
        WAREHOUSE_ROLES,
        `Dashboard ${WAREHOUSES}`,
        "/warehouse-dashboard"
      ),

      dashboardItem(
        "logistics",
        LOGISTICS_ROLES,
        "Dashboard logistique",
        "/logistics-dashboard"
      ),

      dashboardItem(
        "pro",
        PILOTAGE_GLOBAL_ROLES,
        "Dashboard PRO",
        "/dashboard-pro"
      ),

      dashboardItem(
        "reporting",
        PILOTAGE_GLOBAL_ROLES,
        "Reporting",
        "/reporting"
      ),

      dashboardItem(
        "activityLogs",
        [
          "platform_admin",
          "super_admin",
          "dg",
        ],
        "Journal d'activité",
        "/activity-logs"
      ),
    ].filter(Boolean),
  },


  // =======================================================
  // STRUCTURE
  // =======================================================

  {
    title: "Structure",

    items: [
      moduleItem(null, {
        text: "Clients",
        path: "/customers",
        roles: CUSTOMER_ROLES,
      }),

      moduleItem(null, {
        text: `${SHOPS} / Points de vente`,
        path: "/stores",
        roles: STRUCTURE_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // VENTES
  // =======================================================

  {
    title: "Ventes",

    items: [
      moduleItem("sales", {
        text: "Historique des ventes",
        path: "/sales",
        roles: SALES_VIEW_ROLES,
      }),

      moduleItem("sales", {
        text: "Nouvelle vente",
        path: "/sales/new",
        roles: SALES_CREATE_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // ACHATS & FOURNISSEURS
  // =======================================================

  {
    title: "Achats & Fournisseurs",

    roles: PURCHASING_GROUP_ROLES,

    items: [
      moduleItem(null, {
        text: "Fournisseurs",
        path: "/suppliers",
        roles: PURCHASE_VIEW_ROLES,
      }),

      moduleItem(null, {
        text: "Demandes d'achat",
        path: "/purchase-requests",
        roles: PURCHASE_REQUEST_ROLES,
      }),

      moduleItem(null, {
        text: "Bons de commande",
        path: "/purchase-orders",
        roles: PURCHASE_ORDER_ROLES,
      }),

      moduleItem(null, {
        text: "Réceptions fournisseurs",
        path: "/purchase-receipts",
        roles: PURCHASE_RECEIPT_ROLES,
      }),

      moduleItem(null, {
        text: "Factures fournisseurs",
        path: "/supplier-invoices",
        roles: SUPPLIER_INVOICE_ROLES,
      }),

      moduleItem(null, {
        text: "Paiements fournisseurs",
        path: "/supplier-payments",
        roles: SUPPLIER_PAYMENT_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // PRODUCTION
  // =======================================================

  {
  title: "Production",

  items: [
    moduleItem(null, {
      text: "Nomenclatures (BOM)",
      path: "/production/boms",
      roles: PRODUCTION_ROLES,
    }),

    moduleItem(null, {
      text: "Ordres de production",
      path: "/production/orders",
      roles: PRODUCTION_ROLES,
    }),
  ].filter(Boolean),
},


  // =======================================================
  // RESSOURCES HUMAINES
  // =======================================================

  {
    title: "Ressources humaines",

    items: [
      moduleItem(null, {
        text: "Dashboard RH",
        path: "/hr/dashboard",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Employés",
        path: "/hr/employees",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Départements & postes",
        path: "/hr/departments-positions",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Présences",
        path: "/hr/attendance",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Congés & permissions",
        path: "/hr/leaves",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Paie",
        path: "/hr/payroll",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Contrats & documents",
        path: "/hr/contracts-documents",
        roles: HR_ROLES,
      }),

      moduleItem(null, {
        text: "Évaluation & performance",
        path: "/hr/performance",
        roles: HR_ROLES,
      }),
    ].filter(Boolean),
  },



  // =======================================================
  // COMPTABILITÉ & FINANCE
  // =======================================================

  {
    title: "Comptabilité & Finance",

    items: [
      moduleItem(null, {
        text: "Dashboard Finance",
        path: "/accounting/dashboard",
        roles: ACCOUNTING_ROLES,
      }),

      moduleItem(null, {
        text: "Plan comptable",
        path: "/accounting/chart-of-accounts",
        roles: ACCOUNTING_ROLES,
      }),

      moduleItem(null, {
        text: "Écritures comptables",
        path: "/accounting/journal-entries",
        roles: ACCOUNTING_ROLES,
      }),

      moduleItem(null, {
        text: "Grand livre",
        path: "/accounting/general-ledger",
        roles: ACCOUNTING_ROLES,
      }),

      moduleItem(null, {
        text: "Balance comptable",
        path: "/accounting/trial-balance",
        roles: ACCOUNTING_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // PAIEMENTS & CAISSE
  // =======================================================

  {
    title: "Paiements & Caisse",

    items: [
      moduleItem("payments", {
        text: "Paiements clients",
        path: "/payments",
        roles: CASHIER_ROLES,
      }),

      moduleItem("cashDeposits", {
        text: "Dépôts caisse",
        path: "/deposits",
        roles: CASHIER_ROLES,
      }),

      moduleItem("cashDeposits", {
        text: "Rapprochements",
        path: "/store-reconciliations",
        roles: CASHIER_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // DÉPENSES & DETTES
  // =======================================================

  {
    title: "Dépenses & Dettes",

    items: [
      moduleItem("expenses", {
        text: `Dépenses ${SHOP.toLowerCase()}`,
        path: "/expenses",
        roles: EXPENSE_ROLES,
      }),

      moduleItem("debts", {
        text: "Dettes",
        path: "/debts",
        roles: DEBT_ROLES,
      }),

      moduleItem("debts", {
        text: "Règlement des dettes",
        path: "/debt-payments",
        roles: CASHIER_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // STOCK & INVENTAIRE
  // =======================================================

  {
    title: "Stock & Inventaire",

    items: [
      moduleItem("warehouses", {
        text: `Gestion ${WAREHOUSES.toLowerCase()}`,
        path: "/warehouses",
        roles: WAREHOUSE_MANAGE_ROLES,
      }),

      moduleItem("stockOrders", {
        text: "Commandes stock",
        path: "/stock-orders",
        roles: STOCK_ORDER_ROLES,
      }),

      moduleItem("stock", {
        text: "Stock actuel",
        path: "/stock-balances",
        roles: STOCK_VIEW_ROLES,
      }),

      moduleItem("stock", {
        text: "Alertes stock",
        path: "/stock-alerts",
        roles: STOCK_VIEW_ROLES,
      }),

      moduleItem("stock", {
        text: "Mouvements stock",
        path: "/stock-movements",
        roles: STOCK_MANAGER_ROLES,
      }),

      moduleItem("stockTransfers", {
        text: "Transferts stock",
        path: "/stock-transfers",
        roles: STOCK_TRANSFER_ROLES,
      }),

      moduleItem("stockAudits", {
        text: "Audits stock",
        path: "/stock-audits",
        roles: STOCK_AUDIT_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // LOGISTIQUE
  // =======================================================

  {
    title: "Logistique",

    items: [
      moduleItem("vehicles", {
        text: "Véhicules",
        path: "/vehicles",
        roles: LOGISTICS_ROLES,
      }),

      moduleItem("drivers", {
        text: "Chauffeurs",
        path: "/drivers",
        roles: LOGISTICS_ROLES,
      }),

      moduleItem("logistics", {
        text: "Tournées / Livraisons",
        path: "/delivery-trips",
        roles: LOGISTICS_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // CATALOGUE
  // =======================================================

  {
    title: "Catalogue",

    items: [
      moduleItem(null, {
        text: "Catégories",
        path: "/categories",
        roles: CATALOGUE_ROLES,
      }),

      moduleItem(null, {
        text: "Produits",
        path: "/products",
        roles: CATALOGUE_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // PARAMÈTRES
  // =======================================================

  {
    title: "Paramètres",

    items: [
      moduleItem("productPrices", {
        text: "Prix produits",
        path: "/settings/product-prices",
        roles: PRICE_SETTINGS_ROLES,
      }),
    ].filter(Boolean),
  },


  // =======================================================
  // ADMINISTRATION
  // =======================================================

  {
    title: "Administration",

    items: [
      moduleItem("organizations", {
        text: "Organisations",
        path: "/organizations",
        roles: ADMINISTRATION_ROLES,
      }),

      moduleItem("administration", {
        text: "Rôles",
        path: "/roles",
        roles: ADMINISTRATION_ROLES,
      }),

      moduleItem("administration", {
        text: "Utilisateurs",
        path: "/users",
        roles: ADMINISTRATION_ROLES,
      }),
    ].filter(Boolean),
  },
]


// =========================================================
// ROUTE MATCHING
// =========================================================

const routeMatches = (
  pathname,
  path
) => {
  if (path === "/") {
    return pathname === "/"
  }

  return (
    pathname === path ||
    pathname.startsWith(
      `${path}/`
    )
  )
}


// =========================================================
// SIDEBAR
// =========================================================

function Sidebar({
  drawerWidth,
  mobileOpen = false,
  onMobileClose = () => {},
}) {
  const location = useLocation()

  const role =
    localStorage.getItem("kongosys_role") ||
    "viewer"

  const fullName =
    localStorage.getItem("kongosys_full_name") ||
    "Utilisateur"

  const organizationName =
    localStorage.getItem("kongosys_organization_name") ||
    ""

  const storeName =
    localStorage.getItem("kongosys_store_name") ||
    ""


  // =======================================================
  // FILTRAGE SELON LE RÔLE
  // =======================================================

  const visibleGroups = useMemo(() => {
    return menuGroups
      .map((group) => ({
        ...group,

        items: group.items.filter(
          (item) =>
            item.roles.includes(role)
        ),
      }))
      .filter(
        (group) =>
          group.items.length > 0
      )
  }, [role])


  // =======================================================
  // GROUPE ACTIF
  // =======================================================
  //
  // On cherche le chemin le plus spécifique.
  // Cela évite qu'un groupe soit considéré actif simplement
  // parce qu'un chemin plus court ressemble au chemin courant.
  // =======================================================

  const activeGroupTitle = useMemo(() => {
    let bestMatch = null

    visibleGroups.forEach((group) => {
      group.items.forEach((item) => {
        const matches =
          routeMatches(
            location.pathname,
            item.path
          )

        if (!matches) {
          return
        }

        const score =
          item.path.length

        if (
          !bestMatch ||
          score > bestMatch.score
        ) {
          bestMatch = {
            title:
              group.title,
            score,
          }
        }
      })
    })

    return (
      bestMatch?.title ||
      visibleGroups[0]?.title ||
      ""
    )
  }, [
    location.pathname,
    visibleGroups,
  ])


  const [
    openGroup,
    setOpenGroup,
  ] = useState(
    activeGroupTitle
  )

  const [
    showAllModules,
    setShowAllModules,
  ] = useState(false)


  useEffect(() => {
    setOpenGroup(
      activeGroupTitle
    )

    // À chaque changement de page, on revient automatiquement
    // au mode concentré du module correspondant.
    if (
      activeGroupTitle
    ) {
      setShowAllModules(false)
    }
  }, [
    activeGroupTitle,
    location.pathname,
  ])


  const currentOpenGroup =
    openGroup ||
    activeGroupTitle


  const focusMode =
    Boolean(
      activeGroupTitle &&
      !showAllModules
    )


  const groupsToRender =
    focusMode
      ? visibleGroups.filter(
          (group) =>
            group.title ===
            activeGroupTitle
        )
      : visibleGroups


  // =======================================================
  // ITEM ACTIF
  // =======================================================

  const isSelected = (
    path
  ) => {
    return routeMatches(
      location.pathname,
      path
    )
  }


  // =======================================================
  // OUVERTURE / FERMETURE GROUPES
  // =======================================================

  const handleToggle = (
    title
  ) => {
    setOpenGroup(
      (previous) =>
        previous === title
          ? ""
          : title
    )
  }


  // =======================================================
  // MOBILE
  // =======================================================

  const handleMenuItemClick =
    () => {
      onMobileClose()
    }


  // =======================================================
  // STYLE DRAWER
  // =======================================================

  const drawerPaperSx = {
    width: {
      xs: "88vw",
      sm: 320,
      lg: drawerWidth,
    },

    maxWidth: {
      xs: 360,
      lg: drawerWidth,
    },

    boxSizing:
      "border-box",

    background:
      "linear-gradient(180deg, #020617 0%, #0f172a 100%)",

    color:
      "#fff",

    borderRight:
      "1px solid rgba(255,255,255,0.08)",

    boxShadow:
      "10px 0 30px rgba(2,6,23,0.18)",

    overflowX:
      "hidden",
  }


  // =======================================================
  // CONTENU
  // =======================================================

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection:
          "column",
      }}
    >

      {/* ================================================= */}
      {/* LOGO / APPLICATION */}
      {/* ================================================= */}

      <Toolbar
        sx={{
          minHeight: {
            xs: 62,
            lg: 66,
          },

          px: {
            xs: 1.5,
            sm: 1.75,
          },

          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,

            borderRadius: 2,

            backgroundColor:
              "#ffffff",

            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",

            p: 0.7,

            flexShrink: 0,

            boxShadow:
              "0 8px 24px rgba(0,0,0,0.28)",

            border:
              "1px solid rgba(255,255,255,0.7)",
          }}
        >
          <Box
            component="img"
            src={APP_LOGO}
            alt={`Logo ${APP_NAME}`}
            onError={(event) => {
              event.currentTarget.style.display =
                "none"
            }}
            sx={{
              width: "100%",
              height: "100%",
              objectFit:
                "contain",
              display:
                "block",
            }}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={900}
            noWrap
          >
            {APP_NAME}
          </Typography>

          <Typography
            variant="caption"
            noWrap
            sx={{
              color:
                "#94a3b8",
              display:
                "block",
              lineHeight:
                1.2,
            }}
          >
            {APP_SUBTITLE}
          </Typography>

          <Typography
            variant="caption"
            noWrap
            sx={{
              mt: 0.15,
              color:
                "#64748b",
              display:
                "block",
              lineHeight:
                1.15,
              fontSize:
                10.5,
              fontWeight:
                700,
            }}
          >
            ERP opérationnel
          </Typography>
        </Box>
      </Toolbar>


      <Divider
        sx={{
          borderColor:
            "rgba(255,255,255,0.08)",
        }}
      />


      {/* ================================================= */}
      {/* MENU */}
      {/* ================================================= */}

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,

          overflowY:
            "auto",

          overflowX:
            "hidden",

          pb: 2,

          WebkitOverflowScrolling:
            "touch",

          "&::-webkit-scrollbar":
            {
              width: 6,
            },

          "&::-webkit-scrollbar-thumb":
            {
              backgroundColor:
                "rgba(148,163,184,0.35)",

              borderRadius:
                10,
            },
        }}
      >
        {focusMode ? (
          <Box
            sx={{
              px: {
                xs: 1,
                sm: 1.2,
              },
              pt: 1.1,
              pb: 0.65,
            }}
          >
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2.5,
                background:
                  "linear-gradient(135deg, rgba(37,99,235,0.22), rgba(14,165,233,0.10))",
                border:
                  "1px solid rgba(96,165,250,0.18)",
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#93c5fd",
                  fontWeight: 900,
                  letterSpacing: 0.8,
                  lineHeight: 1.2,
                }}
              >
                MODULE ACTIF
              </Typography>

              <Typography
                variant="body2"
                fontWeight={900}
                sx={{
                  mt: 0.45,
                  color: "#fff",
                }}
              >
                {activeGroupTitle}
              </Typography>

              <Button
                fullWidth
                size="small"
                variant="text"
                onClick={() =>
                  setShowAllModules(true)
                }
                sx={{
                  mt: 1,
                  justifyContent: "flex-start",
                  color: "#bfdbfe",
                  fontWeight: 800,
                  textTransform: "none",
                  px: 0,
                  minHeight: 34,
                  "&:hover": {
                    backgroundColor:
                      "rgba(255,255,255,0.05)",
                  },
                }}
              >
                ← Tous les modules
              </Button>
            </Box>
          </Box>
        ) : null}

        {!focusMode ? (
          <Box
            sx={{
              px: {
                xs: 1,
                sm: 1.2,
              },
              pt: 1.1,
              pb: 0.4,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color:
                  "#64748b",
                fontWeight:
                  900,
                letterSpacing:
                  0.8,
                fontSize:
                  10.5,
              }}
            >
              TOUS LES MODULES
            </Typography>
          </Box>
        ) : null}

        <List
          sx={{
            px: {
              xs: 1,
              sm: 1.2,
            },
            pb: 3,
          }}
        >
          {groupsToRender.map(
            (group) => {
              const isOpen =
                focusMode
                  ? true
                  : (
                      currentOpenGroup ===
                      group.title
                    )

              const isActiveGroup =
                group.title ===
                activeGroupTitle


              return (
                <Box
                  key={
                    group.title
                  }
                  sx={{
                    mb: 0.7,
                  }}
                >

                  {/* =============================== */}
                  {/* TITRE GROUPE */}
                  {/* =============================== */}

                  <ListItem
                    disablePadding
                  >
                    <ListItemButton
                      onClick={() => {
                        if (focusMode) {
                          return
                        }

                        handleToggle(
                          group.title
                        )
                      }}

                      sx={{
                        borderRadius:
                          2.2,

                        py: 0.95,

                        minHeight:
                          42,

                        color:
                          "#e5e7eb",

                        backgroundColor:
                          isActiveGroup
                            ? "rgba(37,99,235,0.28)"
                            : "transparent",

                        border:
                          isActiveGroup
                            ? "1px solid rgba(96,165,250,0.16)"
                            : "1px solid transparent",

                        "&:hover":
                          {
                            backgroundColor:
                              "rgba(255,255,255,0.08)",
                          },
                      }}
                    >
                      <ListItemText
                        primary={
                          group.title
                        }

                        primaryTypographyProps={{
                          fontSize:
                            13.5,

                          fontWeight:
                            900,
                        }}
                      />


                      <Typography
                        sx={{
                          fontSize:
                            18,

                          fontWeight:
                            900,

                          color:
                            "#94a3b8",
                        }}
                      >
                        {focusMode
                          ? "•"
                          : (
                              isOpen
                                ? "−"
                                : "+"
                            )}
                      </Typography>
                    </ListItemButton>
                  </ListItem>


                  {/* =============================== */}
                  {/* ITEMS */}
                  {/* =============================== */}

                  <Collapse
                    in={isOpen}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        mt: 0.45,
                        ml: {
                          xs: 0.35,
                          sm: 0.55,
                        },
                        mr: 0.15,
                        mb: 0.65,
                        py: 0.45,
                        px: 0.35,
                        borderRadius: 2.2,

                        // Zone visuelle propre au module actuellement ouvert.
                        // Elle distingue les sous-menus des autres modules,
                        // sans alourdir la navigation.
                        backgroundColor:
                          isActiveGroup
                            ? "rgba(30,64,175,0.12)"
                            : "rgba(15,23,42,0.56)",

                        border:
                          isActiveGroup
                            ? "1px solid rgba(96,165,250,0.13)"
                            : "1px solid rgba(148,163,184,0.05)",

                        boxShadow:
                          isActiveGroup
                            ? "inset 3px 0 0 rgba(56,189,248,0.28)"
                            : "none",
                      }}
                    >
                      <List
                        disablePadding
                        sx={{
                          mt: 0,
                        }}
                      >
                      {group.items.map(
                        (item) => (
                          <ListItem
                            key={`${item.path}-${item.text}`}
                            disablePadding
                            sx={{
                              pl: {
                                xs: 0.65,
                                sm: 0.85,
                              },
                            }}
                          >
                            <ListItemButton
                              component={
                                RouterLink
                              }

                              to={
                                item.path
                              }

                              selected={isSelected(
                                item.path
                              )}

                              onClick={
                                handleMenuItemClick
                              }

                              sx={{
                                borderRadius:
                                  2.2,

                                mb: 0.35,

                                py: {
                                  xs: 1,
                                  md: 0.85,
                                },

                                minHeight: {
                                  xs: 44,
                                  lg: 40,
                                },

                                color:
                                  isOpen
                                    ? "#dbeafe"
                                    : "#cbd5e1",

                                backgroundColor:
                                  isOpen
                                    ? "rgba(255,255,255,0.025)"
                                    : "transparent",

                                border:
                                  "1px solid transparent",

                                "&.Mui-selected":
                                  {
                                    background:
                                      "linear-gradient(90deg, rgba(14,165,233,0.22), rgba(37,99,235,0.12))",

                                    color:
                                      "#ffffff",

                                    borderLeft:
                                      "4px solid #38bdf8",

                                    borderTop:
                                      "1px solid rgba(125,211,252,0.10)",

                                    borderRight:
                                      "1px solid rgba(125,211,252,0.08)",

                                    borderBottom:
                                      "1px solid rgba(125,211,252,0.08)",

                                    boxShadow:
                                      "0 4px 14px rgba(2,132,199,0.10)",
                                  },

                                "&.Mui-selected:hover":
                                  {
                                    background:
                                      "linear-gradient(90deg, rgba(14,165,233,0.28), rgba(37,99,235,0.16))",
                                  },

                                "&:hover":
                                  {
                                    backgroundColor:
                                      isOpen
                                        ? "rgba(59,130,246,0.10)"
                                        : "rgba(255,255,255,0.08)",

                                    color:
                                      "#fff",

                                    borderColor:
                                      isOpen
                                        ? "rgba(96,165,250,0.10)"
                                        : "transparent",
                                  },
                              }}
                            >
                              <ListItemText
                                primary={
                                  item.text
                                }

                                primaryTypographyProps={{
                                  fontSize:
                                    13.5,

                                  letterSpacing:
                                    isSelected(
                                      item.path
                                    )
                                      ? 0.05
                                      : 0,

                                  fontWeight:
                                    isSelected(
                                      item.path
                                    )
                                      ? 900
                                      : 650,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        )
                      )}
                      </List>
                    </Box>
                  </Collapse>
                </Box>
              )
            }
          )}
        </List>
      </Box>
    </Box>
  )


  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {/* ================================================= */}
      {/* MOBILE */}
      {/* ================================================= */}

      <Drawer
        variant="temporary"

        open={
          mobileOpen
        }

        onClose={
          onMobileClose
        }

        ModalProps={{
          keepMounted:
            true,
        }}

        sx={{
          display: {
            xs: "block",
            lg: "none",
          },

          "& .MuiDrawer-paper":
            drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>


      {/* ================================================= */}
      {/* DESKTOP */}
      {/* ================================================= */}

      <Drawer
        variant="permanent"

        open

        sx={{
          display: {
            xs: "none",
            lg: "block",
          },

          width:
            drawerWidth,

          flexShrink:
            0,

          "& .MuiDrawer-paper":
            drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  )
}


export default Sidebar